import { formatDate, parseDateLike } from '../Format/date';
import { NX_WEEKDAY_CODES, weekdayNames, type NxWeekday } from '../WeekdayPicker/weekdays';
import type { NxDateLike } from '../RelativeTime/relativeTime';

/** How often a series repeats; `none` is a single occurrence. */
export type NxRecurrenceFrequency = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

/** An RRULE day code, lower case as `NX_WEEKDAY_CODES` has them („mo“ … „su“). */
export type NxRecurrenceDay = (typeof NX_WEEKDAY_CODES)[number];

/**
 * A repeat rule, shaped like RFC 5545's RRULE:
 * - `byDay`: weekly — the days; monthly by weekday — the one weekday.
 * - `byMonthDay`: monthly — the day of the month (1–31).
 * - `bySetPosition`: monthly by weekday — which one (1–4, `-1` = the last).
 * - `byMonth`: yearly — the months (1–12); empty means the start's month.
 * - `count` / `until` (`YYYY-MM-DD`): how the series ends; neither = never.
 */
export interface NxRecurrenceRule {
  frequency: NxRecurrenceFrequency;
  interval: number;
  byDay?: NxRecurrenceDay[];
  byMonthDay?: number | null;
  bySetPosition?: number[];
  byMonth?: number[];
  count?: number | null;
  until?: string | null;
}

export type NxRecurrenceEnd = 'never' | 'count' | 'until';

export const NX_RECURRENCE_FREQUENCIES: readonly NxRecurrenceFrequency[] = [
  'none',
  'daily',
  'weekly',
  'monthly',
  'yearly',
];

/** Positions offered for „am n. Wochentag“; `-1` is the last. */
export const NX_RECURRENCE_POSITIONS: readonly number[] = [1, 2, 3, 4, -1];

/** A rule that does not repeat. */
export function createRecurrenceRule(frequency: NxRecurrenceFrequency = 'none'): NxRecurrenceRule {
  return { frequency, interval: 1, byDay: [], byMonthDay: null, bySetPosition: [], byMonth: [] };
}

export function isRecurrenceActive(rule: NxRecurrenceRule | null | undefined): boolean {
  return !!rule && rule.frequency !== 'none';
}

/** How the series ends, read off `until` / `count`. */
export function recurrenceEnd(rule: NxRecurrenceRule | null | undefined): NxRecurrenceEnd {
  if (rule?.until) return 'until';
  if (rule?.count) return 'count';
  return 'never';
}

/** Monthly by the nth weekday (`bySetPosition` set) rather than by the day of the month. */
export function isMonthlyByWeekday(rule: NxRecurrenceRule | null | undefined): boolean {
  return (rule?.bySetPosition?.length ?? 0) > 0;
}

/** The RRULE code of a JavaScript weekday (0 = Sunday). */
export function weekdayCode(day: number): NxRecurrenceDay {
  return NX_WEEKDAY_CODES[((day % 7) + 7) % 7]!;
}

/** The JavaScript weekday (0 = Sunday) of an RRULE code, `-1` for anything else. */
export function weekdayFromCode(code: string): NxWeekday | -1 {
  const index = (NX_WEEKDAY_CODES as readonly string[]).indexOf(code.toLowerCase());
  return index < 0 ? -1 : (index as NxWeekday);
}

/** Which occurrence of its weekday a date is in its month: 1–4, or `-1` for a fifth (the last). */
export function weekdayPosition(date: Date): number {
  const nth = Math.ceil(date.getDate() / 7);
  return nth >= 5 ? -1 : nth;
}

/** The local calendar date as `YYYY-MM-DD` (no UTC shift). */
export function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export interface NxDescribeRecurrenceOptions {
  /** BCP-47 tag; German wording for `de*`, English otherwise. */
  locale?: string;
  /** The first occurrence — names the day when the rule leaves it open. */
  startDate?: NxDateLike;
}

const GERMAN = {
  every: {
    daily: ['Täglich', (n: number) => `Jeden ${n}. Tag`],
    weekly: ['Wöchentlich', (n: number) => `Jede ${n}. Woche`],
    monthly: ['Monatlich', (n: number) => `Jeden ${n}. Monat`],
    yearly: ['Jährlich', (n: number) => `Jedes ${n}. Jahr`],
  },
  positions: { 1: 'ersten', 2: 'zweiten', 3: 'dritten', 4: 'vierten', [-1]: 'letzten' } as Record<
    number,
    string
  >,
  onDays: (days: string) => ` am ${days}`,
  onMonthDay: (day: number) => ` am ${day}.`,
  onNth: (position: string, day: string) => ` am ${position} ${day}`,
  inMonths: (months: string) => ` im ${months}`,
  until: (date: string) => `, bis ${date}`,
  count: (n: number) => (n === 1 ? ', einmal' : `, ${n} Mal`),
} as const;

const ENGLISH = {
  every: {
    daily: ['Daily', (n: number) => `Every ${n} days`],
    weekly: ['Weekly', (n: number) => `Every ${n} weeks`],
    monthly: ['Monthly', (n: number) => `Every ${n} months`],
    yearly: ['Yearly', (n: number) => `Every ${n} years`],
  },
  positions: { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', [-1]: 'last' } as Record<
    number,
    string
  >,
  onDays: (days: string) => ` on ${days}`,
  onMonthDay: (day: number) => ` on day ${day}`,
  onNth: (position: string, day: string) => ` on the ${position} ${day}`,
  inMonths: (months: string) => ` in ${months}`,
  until: (date: string) => `, until ${date}`,
  count: (n: number) => (n === 1 ? ', once' : `, ${n} times`),
} as const;

function joinList(items: string[], locale: string): string {
  if (items.length <= 1) return items[0] ?? '';
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

function monthNames(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, m) =>
    format.format(new Date(Date.UTC(2024, m, 1))).replace(/\.$/, '')
  );
}

/**
 * The rule in one sentence: „Jede 2. Woche am Mo und Mi, bis 31.12.2026“,
 * „Monatlich am letzten Freitag, 10 Mal“ — German for a `de` locale, English
 * otherwise. `''` for a rule that does not repeat.
 */
export function describeRecurrence(
  rule: NxRecurrenceRule | null | undefined,
  options: NxDescribeRecurrenceOptions = {}
): string {
  if (!rule || rule.frequency === 'none') return '';
  const locale = options.locale ?? 'de-DE';
  const words = locale.toLowerCase().startsWith('de') ? GERMAN : ENGLISH;
  const interval = Math.max(1, Math.trunc(Number(rule.interval) || 1));
  const [once, every] = words.every[rule.frequency];
  let text = interval === 1 ? once : every(interval);

  const start = parseDateLike(options.startDate ?? null);
  const shortDays = weekdayNames(locale, 'short');
  const longDays = weekdayNames(locale, 'long');
  const days = (rule.byDay ?? []).map(weekdayFromCode).filter((day): day is NxWeekday => day >= 0);

  if (rule.frequency === 'weekly') {
    const shown = days.length > 0 ? days : start ? [start.getDay() as NxWeekday] : [];
    // Monday-first, the way the picker shows them.
    const ordered = [...new Set(shown)].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
    if (ordered.length > 0) {
      text += words.onDays(
        joinList(
          ordered.map((day) => shortDays[day]!),
          locale
        )
      );
    }
  } else if (rule.frequency === 'monthly') {
    const position = rule.bySetPosition?.[0];
    if (position !== undefined && days.length > 0) {
      text += words.onNth(words.positions[position] ?? `${position}.`, longDays[days[0]!]!);
    } else {
      const day = rule.byMonthDay ?? start?.getDate();
      if (day) text += words.onMonthDay(day);
    }
  } else if (rule.frequency === 'yearly') {
    const months = [...new Set(rule.byMonth ?? [])].filter((m) => m >= 1 && m <= 12);
    if (months.length > 0) {
      const names = monthNames(locale);
      text += words.inMonths(
        joinList(
          months.sort((a, b) => a - b).map((m) => names[m - 1]!),
          locale
        )
      );
    }
  }

  if (rule.until) {
    const until = formatDate(rule.until, { locale });
    if (until) text += words.until(until);
  } else if (rule.count) {
    const count = Math.trunc(Number(rule.count));
    if (count > 0) text += words.count(count);
  }
  return text;
}
